import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, of, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs/operators';
import { SessionQuery } from '../../state-mangement/session/session.query';
import { SettingsService } from '../../state-mangement/settings/settings.service';
import { AppConfig } from '../../services/config.service';
import { ThemeService } from '../../services/theme/theme.service';
import { BadgeService } from '../badge/badge.service';
import { BadgeQuery } from '../badge/badge.query';

@Injectable({ providedIn: 'root' })
export class SettingsKvInitService {
  private readonly urlGetSetting =
    (AppConfig?.settings?.backend?.url || '/api/') + 'Settings/getSetting';
  private readonly urlGetSettings =
    (AppConfig?.settings?.backend?.url || '/api/') + 'Settings/getSettings';

  private _initStarted = false;

  constructor(
    private http: HttpClient,
    private sessionQuery: SessionQuery,
    private themeService: ThemeService,
    private settingsService: SettingsService,
    private badgeService: BadgeService,
    private badgeQuery: BadgeQuery
  ) {}

  /** direkt nach Login aufrufen */
  initOnLogin() {
    if (this._initStarted) return;
    this._initStarted = true;

    forkJoin({
      dark: this.loadAppearance_DarkMode(),
      theme: this.loadAppearance_Theme(),

      // === GENERAL FLAGS ===
      showOperation: this.loadGeneralBool('ShowOperation'),
      showMultiOrder: this.loadGeneralBool('Multiorder'),
      showBarcodeScanner: this.loadGeneralBool('BarcodeScanner'),
      showTimeInEdit: this.loadGeneralBool('OnTimeHours'),
    }).subscribe({
      next: (vals) => {
        // Theme anwenden
        this.themeService.updateThemeModeRef(!!vals.dark);
        this.settingsService.setThemeMode(!!vals.dark);
        const theme = (vals.theme === 'modern' ? 'modern' : 'admia') as
          | 'admia'
          | 'modern';
        this.themeService.updateThemeRef(theme);
        this.settingsService.setTheme(theme);

        // General-Flags in den Settings-Store setzen
        if (typeof vals.showOperation === 'boolean') {
          this.settingsService.setShowOperation(vals.showOperation);
        }
        if (typeof vals.showMultiOrder === 'boolean') {
          this.settingsService.setShowMultiOrder(vals.showMultiOrder);
        }
        if (typeof vals.showBarcodeScanner === 'boolean') {
          this.settingsService.setShowBarcodeScanner(vals.showBarcodeScanner);
        }
        if (typeof vals.showTimeInEdit === 'boolean') {
          this.settingsService.setShowTimeInEdit(vals.showTimeInEdit);
        }
      },
      error: (e) => console.warn('[KV-INIT] appearance/general error', e),
    });

    // 2) Badge-Grid nach gültiger Person laden
    this.sessionQuery.idPerson$
      .pipe(
        filter((id) => !!id && id !== 0),
        take(1),
        switchMap(() => this.loadBadgeFromKv$())
      )
      .subscribe({
        error: (e) => console.warn('[KV-INIT] badge load failed', e),
      });
  }

  // ===== Helpers =====

  private paramsWithIdentity(groupKey: string, nameKey: string): HttpParams {
    let p = new HttpParams().set('GroupKey', groupKey).set('NameKey', nameKey);
    const pid = this.sessionQuery.getPersonID();
    const uid = this.sessionQuery.getAdmiaUserID();
    if (pid) p = p.set('IDPerson', String(pid));
    if (uid) p = p.set('IDAdmiaUser', String(uid));
    return p;
  }

  // ---------- Appearance ----------
  private loadAppearance_DarkMode() {
    const params = this.paramsWithIdentity('Appearance', 'DarkMode');
    return this.http.get<any>(this.urlGetSetting, { params }).pipe(
      map((res) => this.parseBoolFromSetting(res, false)),
      catchError((err) => {
        console.warn('[KV-INIT] DarkMode failed', err);
        return of(false);
      })
    );
  }

  private loadAppearance_Theme() {
    const params = this.paramsWithIdentity('Appearance', 'Theme');
    return this.http.get<any>(this.urlGetSetting, { params }).pipe(
      map((res) => (res?.valueString as 'admia' | 'modern') || 'admia'),
      catchError((err) => {
        console.warn('[KV-INIT] Theme failed', err);
        return of<'admia' | 'modern'>('admia');
      })
    );
  }

  // ---------- General ----------
  private loadGeneralBool(
    nameKey: 'ShowOperation' | 'Multiorder' | 'BarcodeScanner' | 'OnTimeHours'
  ) {
    const params = this.paramsWithIdentity('General', nameKey);
    return this.http.get<any>(this.urlGetSetting, { params }).pipe(
      map((res) =>
        this.parseBoolFromSetting(res, undefined /* no fallback here */)
      ),
      catchError((err) => {
        console.warn(`[KV-INIT] General ${nameKey} failed`, err);
        return of<boolean | undefined>(undefined);
      })
    );
  }

  /** Bool-Parser: bevorzugt ValueBool */
  private parseBoolFromSetting(res: any, fallback?: boolean): boolean {
    if (typeof res?.valueBool === 'boolean') return res.valueBool;

    const vs = res?.valueString;
    if (typeof vs === 'string') {
      try {
        const j = JSON.parse(vs);
        if (typeof j?.selected === 'boolean') return j.selected;
      } catch {
        const low = vs.trim().toLowerCase();
        if (low === 'true' || low === '1') return true;
        if (low === 'false' || low === '0') return false;
      }
    }
    if (typeof fallback === 'boolean') return fallback;
    return undefined as any;
  }

  // ---------- Badge-Grid ----------
  private loadBadgeFromKv$() {
    const pid = this.sessionQuery.getPersonID();
    const uid = this.sessionQuery.getAdmiaUserID();
    let params = new HttpParams().set('GroupKey', 'Badge');
    if (pid) params = params.set('IDPerson', String(pid));
    if (uid) params = params.set('IDAdmiaUser', String(uid));

    return this.http.get<any[]>(this.urlGetSettings, { params }).pipe(
      timeout(15000),

      map((rows) => {
        const mapByName = new Map<string, any>();
        rows?.forEach((r) => {
          try {
            mapByName.set(r.nameKey, JSON.parse(r.valueString || '{}'));
          } catch {}
        });

        const current = this.badgeQuery.getSettings() ?? [];
        const merged = current
          .map((item) => {
            const v = mapByName.get(item.name);
            return v
              ? {
                  ...item,
                  selected:
                    typeof v.selected === 'boolean'
                      ? v.selected
                      : item.selected,
                  position: Number.isFinite(v.position)
                    ? v.position
                    : item.position,
                  disabled:
                    typeof v.disabled === 'boolean'
                      ? v.disabled
                      : item.disabled,
                }
              : item;
          })
          .sort((a, b) => a.position - b.position);

        return merged;
      }),
      tap((merged) => this.badgeService.saveSettings(merged)),
      catchError((err) => {
        console.warn('[KV-INIT] loadBadgeFromKv$ error', err);
        return throwError(() => err);
      })
    );
  }
}
